/**
 * lib/bars.ts — the ONE OHLCV fetcher.
 *
 * MUST be the only queryFn used for the ['bars', focus, period, interval]
 * React Query key. Two components read that key: MainChart (the candles) and
 * ChartHeader (the LAST/CHG/BAR-HI/LO/VOL/RSI/ATR stats line). React Query
 * dedupes by key but keeps ONE queryFn per key -- whichever query instance
 * registers last wins. When the two fetchers differed, ChartHeader's raw
 * `() => api(...)` could win and hand MainChart un-normalized bars (short field
 * names, string numbers, or a non-finite timestamp), which throws in
 * toISOString and blanks the whole chart -- exactly the crash MainChart's own
 * inline normalize was written to prevent, silently reintroduced through the
 * shared cache. One fetcher, one shape, no collision.
 *
 * The endpoint may return Bar[] or { bars: Bar[] }, with long OR short field
 * names. Normalize once, coerce to numbers, and DROP any bar whose time or
 * close isn't finite. A bad response must degrade to fewer/zero bars, never a
 * throw.
 */
import { api } from '@/lib/api';
import type { Bar } from '@/types';

export async function fetchBars(focus: string, period: string, interval: string): Promise<Bar[]> {
  const raw = await api<Bar[] | { bars?: Bar[] }>(
    `/api/ohlcv/${encodeURIComponent(focus)}?period=${period}&interval=${interval}`,
  );
  const arr = (Array.isArray(raw) ? raw : raw?.bars ?? []) as unknown as Array<Record<string, unknown>>;
  const n = (v: unknown) => Number(v);
  return arr
    .map(b => ({
      time:   n(b.time   ?? b.t),
      open:   n(b.open   ?? b.o),
      high:   n(b.high   ?? b.h),
      low:    n(b.low    ?? b.l),
      close:  n(b.close  ?? b.c),
      volume: n(b.volume ?? b.v ?? 0),
    }))
    .filter(b => Number.isFinite(b.time) && Number.isFinite(b.close));
}
